/**
 * Next fixed tournament match for a player once a draw is out.
 * Presentation input only — competitions are already loaded.
 */

import { DRAW_STATUSES } from "../competition/drawEngineTypes";
import { ENTRY_STATUSES } from "../competition/entryManagementTypes";
import { formatSidePlayerLabel } from "../matchHistory/matchHistoryPresentation";

const PUBLISHED_DRAW = new Set([
  DRAW_STATUSES.GENERATED,
  DRAW_STATUSES.LOCKED,
  DRAW_STATUSES.COMPLETED,
]);

const LOCKED_DRAW = new Set([DRAW_STATUSES.LOCKED, DRAW_STATUSES.COMPLETED]);

const CLOSED_FIXTURE = new Set(["completed", "done", "bye", "walkover"]);

function eventName(competition, eventId) {
  const event = (competition?.managedEvents || []).find((row) => row.eventId === eventId);
  return event?.name || event?.display?.name || "";
}

function isOpenFixedFixture(fixture, entryIds) {
  if (!fixture || fixture.isBye) return false;
  const status = String(fixture.status || "pending").toLowerCase();
  if (CLOSED_FIXTURE.has(status)) return false;
  if (fixture.winnerEntryId) return false;
  if (!fixture.entryAId || !fixture.entryBId) return false;
  return entryIds.has(fixture.entryAId) || entryIds.has(fixture.entryBId);
}

function urgency(fixture) {
  const queue = String(fixture.queueStatus || fixture.status || "").toLowerCase();
  if (queue === "playing" || queue === "live") return 0;
  if (queue === "ready" || queue === "called") return 1;
  return 2;
}

function roundPhrase(hit) {
  const numbered =
    Number.isFinite(hit.roundIndex) && hit.roundIndex < 99 ? `Round ${hit.roundIndex + 1}` : "";
  const name = String(hit.roundName || "").trim();
  const named = name && !/^round\s+\d+$/i.test(name) && !/^match$/i.test(name) ? name : "";
  return [numbered, named].filter(Boolean).join(" · ") || "Round 1";
}

function fixtureGraphHref(competitionId, drawId) {
  if (competitionId && drawId) {
    return `/browse/${encodeURIComponent(competitionId)}/fixtures/${encodeURIComponent(drawId)}`;
  }
  if (competitionId) return `/browse/${encodeURIComponent(competitionId)}/fixtures`;
  return "/tournaments";
}

/**
 * Open, fixed fixtures for a player, soonest first.
 * @param {object[] | null | undefined} competitions
 * @param {string | null | undefined} playerId
 */
function collectOpenFixtures(competitions, playerId) {
  if (!playerId) return [];
  /** @type {object[]} */
  const hits = [];

  (competitions || []).forEach((competition) => {
    const mine = (competition?.managedEntries || []).filter((entry) => {
      const status = String(entry?.status || "").toLowerCase();
      if (status === ENTRY_STATUSES.WITHDRAWN || status === ENTRY_STATUSES.CANCELLED) return false;
      return (entry.memberPlayerIds || []).includes(playerId);
    });
    if (!mine.length) return;
    const entryIds = new Set(mine.map((entry) => entry.entryId).filter(Boolean));
    const byEntry = new Map(mine.map((entry) => [entry.entryId, entry]));

    (competition.managedDraws || []).forEach((draw) => {
      const drawStatus = String(draw?.status || "").toLowerCase();
      if (!PUBLISHED_DRAW.has(drawStatus)) return;
      (draw.fixtures || []).forEach((fixture) => {
        if (!isOpenFixedFixture(fixture, entryIds)) return;
        const mineIsA = entryIds.has(fixture.entryAId);
        const myEntry = byEntry.get(mineIsA ? fixture.entryAId : fixture.entryBId);
        const opponentEntryId = mineIsA ? fixture.entryBId : fixture.entryAId;
        const opponentEntry = (competition.managedEntries || []).find(
          (entry) => entry.entryId === opponentEntryId
        );
        const opponentRaw = mineIsA ? fixture.labelB : fixture.labelA;
        const singles =
          myEntry?.category === "singles" || opponentEntry?.category === "singles";
        const opponent = formatSidePlayerLabel(
          [opponentEntry?.label || opponentRaw],
          { singles: singles || opponentEntry?.category === "singles" }
        );
        const eventId = fixture.eventId || draw.eventId || myEntry?.eventId || null;
        const roomId = competition.liveSessionId || competition.roomId || null;
        const competitionId = competition.competitionId || competition.id || null;
        const court =
          fixture.court ||
          fixture.courtName ||
          (fixture.courtNumber != null ? `Court ${fixture.courtNumber}` : null);
        hits.push({
          competitionId,
          competitionName: competition.name || "Tournament",
          drawId: draw.drawId || null,
          drawStatus,
          fixtureId: fixture.fixtureId || null,
          eventId,
          eventName: eventName(competition, eventId),
          roundName: fixture.roundName || "Match",
          opponentLabel: opponent || "Opponent",
          courtLabel: court,
          scheduledTimeMs: fixture.scheduledTimeMs || null,
          urgency: urgency(fixture),
          roundIndex: Number.isFinite(fixture.roundIndex) ? fixture.roundIndex : 99,
          href: roomId
            ? `/room/${encodeURIComponent(roomId)}/session?tab=overview`
            : competitionId
              ? `/browse/${encodeURIComponent(competitionId)}`
              : "/tournaments",
        });
      });
    });
  });

  hits.sort((a, b) => {
    if (a.urgency !== b.urgency) return a.urgency - b.urgency;
    const aTime = a.scheduledTimeMs || Number.MAX_SAFE_INTEGER;
    const bTime = b.scheduledTimeMs || Number.MAX_SAFE_INTEGER;
    if (aTime !== bTime) return aTime - bTime;
    return a.roundIndex - b.roundIndex;
  });
  return hits;
}

/**
 * @param {object[] | null | undefined} competitions
 * @param {string | null | undefined} playerId
 */
export function projectPlayerNextFixture(competitions, playerId) {
  const hit = collectOpenFixtures(competitions, playerId)[0];
  if (!hit) return null;
  return {
    competitionId: hit.competitionId,
    competitionName: hit.competitionName,
    eventId: hit.eventId,
    eventName: hit.eventName,
    roundName: hit.roundName,
    opponentLabel: hit.opponentLabel,
    courtLabel: hit.courtLabel,
    scheduledTimeMs: hit.scheduledTimeMs,
    urgency: hit.urgency,
    roundIndex: hit.roundIndex,
    href: hit.href,
  };
}

/**
 * One banner per locked draw the player still has an open match in.
 * Generated draws stay off the banner until the host locks them.
 * @param {object[] | null | undefined} competitions
 * @param {string | null | undefined} playerId
 */
export function projectPlayerLockedDrawBanners(competitions, playerId) {
  const seen = new Set();
  const banners = [];
  collectOpenFixtures(competitions, playerId).forEach((hit) => {
    if (!LOCKED_DRAW.has(hit.drawStatus)) return;
    const key = hit.drawId || `${hit.competitionId}:${hit.eventId}`;
    if (seen.has(key)) return;
    seen.add(key);
    const phrase = roundPhrase(hit);
    const court = hit.courtLabel ? `, ${hit.courtLabel}` : "";
    const called = hit.urgency <= 1;
    banners.push({
      competitionId: hit.competitionId,
      competitionName: hit.competitionName,
      eventName: hit.eventName,
      drawId: hit.drawId,
      fixtureId: hit.fixtureId,
      opponentLabel: hit.opponentLabel,
      courtLabel: hit.courtLabel || null,
      roundNumber: Number.isFinite(hit.roundIndex) && hit.roundIndex < 99 ? hit.roundIndex + 1 : null,
      roundName: hit.roundName,
      called,
      badge: called ? "You're up" : "Draw locked",
      cta: called ? "Open match" : "Fixture graph",
      detail: called
        ? `You're up against ${hit.opponentLabel}, ${phrase}${court}.`
        : `Your match is against ${hit.opponentLabel}, ${phrase}${court}.`,
      href: called ? hit.href : fixtureGraphHref(hit.competitionId, hit.drawId),
    });
  });
  return banners;
}
